import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

export interface ConfigState {
	selectedCourses: string[];
	selectedCourse: string;
	selectedStream: string;
	numberOfCourses: number;
	showCoursesMultiSelect: boolean;
	// Multi-course flow state
	currentCourseIndex: number;
	completedCourses: string[];
	totalCoursesToAdd: number;
	// Track added courses to prevent re-selection
	addedCourses: string[];
	// Course preferences state
	preferences: {
		selectedSectors: string[];
		selectedSectorIds: string[];
		locationData: {
			province: string;
			division: string;
			district: string;
			city: string;
		};
	};
}

const initialState: ConfigState = {
	selectedCourses: [],
	selectedCourse: "",
	selectedStream: "",
	numberOfCourses: 1,
	showCoursesMultiSelect: false,
	// Multi-course flow state
	currentCourseIndex: 0,
	completedCourses: [],
	totalCoursesToAdd: 0,
	// Track added courses to prevent re-selection
	addedCourses: [],
	// Course preferences state
	preferences: {
		selectedSectors: [],
		selectedSectorIds: [],
		locationData: {
			province: "",
			division: "",
			district: "",
			city: "",
		},
	},
};

const courseSlice = createSlice({
	name: "course",
	initialState,
	reducers: {
		setSelectedCourses(state, action: PayloadAction<string[] | string>) {
			if (Array.isArray(action.payload)) {
				// If payload is array, replace the entire array
				state.selectedCourses = action.payload;
			} else {
				// If payload is string, push to array (avoiding duplicates)
				if (!state.selectedCourses.includes(action.payload)) {
					state.selectedCourses.push(action.payload);
				} else {
					// If course already exists, move it to the end (most recent)
					state.selectedCourses = state.selectedCourses.filter(course => course !== action.payload);
					state.selectedCourses.push(action.payload);
				}
			}
		},
		setSelectedCourse(state, action: PayloadAction<string>) {
			state.selectedCourse = action.payload;
		},
		setSelectedStream(state, action: PayloadAction<string>) {
			state.selectedStream = action.payload;
		},
		setNumberOfCourses(state, action: PayloadAction<number>) {
			state.numberOfCourses = action.payload;
		},
		// Multi-course flow actions
		setCurrentCourseIndex(state, action: PayloadAction<number>) {
			state.currentCourseIndex = action.payload;
		},
		addCompletedCourse(state, action: PayloadAction<string>) {
			if (!state.completedCourses.includes(action.payload)) {
				state.completedCourses.push(action.payload);
			}
		},
		setTotalCoursesToAdd(state, action: PayloadAction<number>) {
			state.totalCoursesToAdd = action.payload;
		},
		resetMultiCourseFlow(state) {
			state.currentCourseIndex = 0;
			state.completedCourses = [];
			state.totalCoursesToAdd = 0;
		},
		// Course preferences actions
		setSelectedSectors(state, action: PayloadAction<string[]>) {
			state.preferences.selectedSectors = action.payload;
		},
		setSelectedSectorIds(state, action: PayloadAction<string[]>) {
			state.preferences.selectedSectorIds = action.payload;
		},
		setLocationData(state, action: PayloadAction<{ province: string; division: string; district: string; city: string }>) {
			state.preferences.locationData = action.payload;
		},
		updateLocationField(state, action: PayloadAction<{ field: string; value: string }>) {
			const { field, value } = action.payload;
			state.preferences.locationData = {
				...state.preferences.locationData,
				[field]: value,
			};
		},
		clearPreferences(state) {
			state.preferences = {
				selectedSectors: [],
				selectedSectorIds: [],
				locationData: {
					province: "",
					division: "",
					district: "",
					city: "",
				},
			};
		},

		clearSelectedCourses(state) {
			state.selectedCourses = [];
		},
		addCourseToAddedList(state, action: PayloadAction<string>) {
			if (!state.addedCourses.includes(action.payload)) {
				state.addedCourses.push(action.payload);
			}
		},
		clearAddedCourses(state) {
			state.addedCourses = [];
		},
	},
});

export const {
	setSelectedCourses,
	clearSelectedCourses,
	setSelectedCourse,
	setSelectedStream,
	setNumberOfCourses,
	setCurrentCourseIndex,
	addCompletedCourse,
	setTotalCoursesToAdd,
	resetMultiCourseFlow,
	addCourseToAddedList,
	clearAddedCourses,
	// Course preferences actions
	setSelectedSectors,
	setSelectedSectorIds,
	setLocationData,
	updateLocationField,
	clearPreferences
} = courseSlice.actions;

export default courseSlice.reducer;
